/**
 * [PURPOSE]: Pure functional recharge logic. Given inventory + days
 *   passed, returns updated inventory + the list of recharge events
 *   that should be emitted.
 * [WHY]: Previously inline in CalendarPanel.jumpToDate. Moving to a
 *   pure function lets it be (a) tested without React, (b) reused by
 *   the DAY_ADVANCED subscriber so every day-advance path triggers
 *   recharge — not just calendar manual jumps.
 * [INTEGRATION]: Called by InventoryContext's DAY_ADVANCED subscriber.
 *   The subscriber owns setInventory + ITEM_RECHARGED emission.
 * [SPEC]: docs/superpowers/specs/2026-04-28-r1-release-readiness-design.md §D2
 */
import { RECHARGE_TYPES } from '../data/rechargeTypes';

/**
 * Apply day-based recharge to all eligible items in the inventory.
 *
 * @param {Array} inventory   Current inventory array.
 * @param {number} daysPassed Number of days that just passed (>=0).
 * @param {Date|number|string} [gameTime] The in-game clock value (DAY_ADVANCED
 *   payload's `currentTime`). Rule 20: `lastRecharged` must be stamped with the
 *   game clock, NOT real wall-clock — otherwise a future consumer diffing it
 *   against `timeSystem.currentTime` reads garbage (the class RestPanel.js:244
 *   already fixed for rests). Falls back to wall-clock only if not supplied.
 * @returns {{ updatedInventory: Array, rechargeEvents: Array }}
 *   updatedInventory: new array if any item changed; same reference if no change.
 *   rechargeEvents: { item, oldCharges, newCharges, rechargeType } per item changed.
 */
export function applyRechargeForDays(inventory, daysPassed, gameTime) {
  if (!Array.isArray(inventory) || inventory.length === 0 || daysPassed <= 0) {
    return { updatedInventory: inventory, rechargeEvents: [] };
  }

  const rechargeEvents = [];
  let anyChange = false;
  // [GAME-CLOCK 2026-07-13]: stamp with the in-game clock, mirroring RestPanel's
  // [TIER 1.3 FIX]. gameTime comes from the DAY_ADVANCED payload's currentTime.
  const now = gameTime != null
    ? (gameTime instanceof Date ? gameTime : new Date(gameTime))
    : new Date();

  const updatedInventory = inventory.map((item) => {
    const itemShouldRecharge =
      item.rechargeType === RECHARGE_TYPES.DAWN ||
      item.rechargeType === RECHARGE_TYPES.DAILY ||
      (item.rechargeType === RECHARGE_TYPES.WEEKLY && daysPassed >= 7) ||
      (item.rechargeType === RECHARGE_TYPES.MONTHLY && daysPassed >= 30);

    let updated = item;
    let itemChanged = false;

    if (
      itemShouldRecharge &&
      typeof item.maxCharges === 'number' &&
      typeof item.currentCharges === 'number' &&
      item.currentCharges < item.maxCharges
    ) {
      updated = {
        ...item,
        currentCharges: item.maxCharges,
        lastRecharged: now.toISOString(),
      };
      itemChanged = true;
      rechargeEvents.push({
        item: updated,
        oldCharges: item.currentCharges,
        newCharges: item.maxCharges,
        rechargeType: item.rechargeType,
      });
    }

    // Spell-attached recharge for items that hold spells (wands, staves, etc.)
    if (Array.isArray(item.spells) && item.spells.length > 0) {
      const rechargedSpells = item.spells.map((spell) => {
        const spellShouldRecharge =
          spell.rechargeType === RECHARGE_TYPES.DAWN ||
          spell.rechargeType === RECHARGE_TYPES.DAILY ||
          (spell.rechargeType === RECHARGE_TYPES.WEEKLY && daysPassed >= 7) ||
          (spell.rechargeType === RECHARGE_TYPES.MONTHLY && daysPassed >= 30);

        if (
          spellShouldRecharge &&
          typeof spell.currentUses === 'number' &&
          typeof spell.usesPerDay === 'number' &&
          spell.currentUses < spell.usesPerDay
        ) {
          return { ...spell, currentUses: spell.usesPerDay };
        }
        return spell;
      });
      const spellsChanged = rechargedSpells.some(
        (s, i) => s.currentUses !== item.spells[i].currentUses
      );
      if (spellsChanged) {
        updated = { ...updated, spells: rechargedSpells };
        itemChanged = true;
      }
    }

    if (itemChanged) {
      anyChange = true;
    }
    return updated;
  });

  return {
    updatedInventory: anyChange ? updatedInventory : inventory,
    rechargeEvents,
  };
}
